import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Filter, X, CalendarRange, ChevronDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';

/**
 * Generic per-module filter bar — supports multi-select + autocomplete for all filter types.
 * Props:
 *  - filters: object of current filter values { key: value }  (value = comma-separated string for multi)
 *  - onChange: (key, value) => void   (null to clear)
 *  - onReset: () => void
 *  - filterDefs: array of { key, label, type: 'text'|'select'|'date', fetchFn?, options? }
 */
export function ModuleFilterBar({ filters = {}, onChange, onReset, filterDefs = [] }) {
  const activeCount = Object.values(filters).filter(Boolean).length;

  return (
    <div
      className="flex items-center gap-2 p-3 rounded-xl border overflow-x-auto no-scrollbar"
      style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}
      data-testid="module-filter-bar"
    >
      <div className="flex items-center gap-1.5 mr-1 shrink-0">
        <Filter size={12} style={{ color: 'var(--vz-text-muted)' }} />
        <span className="text-[10px] font-montserrat uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>
          Filtros
        </span>
        {activeCount > 0 && (
          <Badge className="text-[9px] h-4 px-1.5" style={{ background: 'rgba(74,158,255,0.2)', color: '#4A9EFF', border: '1px solid rgba(74,158,255,0.3)' }}>
            {activeCount}
          </Badge>
        )}
      </div>

      {filterDefs.map((def) => (
        <FilterField
          key={def.key}
          def={def}
          value={filters[def.key] || ''}
          onChange={(v) => onChange(def.key, v || null)}
        />
      ))}

      {activeCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 text-xs ml-auto shrink-0"
          onClick={onReset}
          style={{ color: 'var(--vz-text-muted)' }}
          data-testid="module-filter-reset"
        >
          <X size={11} /> Limpiar
        </Button>
      )}
    </div>
  );
}

// ── Single filter field ────────────────────────────────────────────────────────
function FilterField({ def, value, onChange }) {
  const { data: apiOptions = [] } = useQuery({
    queryKey: ['filter-options', def.key],
    queryFn: def.fetchFn ?? (() => Promise.resolve([])),
    staleTime: 10 * 60 * 1000,
    enabled: !!def.fetchFn,
  });

  const options = def.options || apiOptions;

  if (def.type === 'date') {
    return (
      <div className="flex items-center gap-1 shrink-0">
        <CalendarRange size={11} style={{ color: 'var(--vz-text-muted)' }} />
        <Input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={def.label}
          className="h-7 text-xs w-32 border"
          style={{
            background: 'var(--vz-bg-card)',
            borderColor: 'var(--vz-border-subtle)',
            color: 'var(--vz-text-primary)',
          }}
          data-testid={`filter-${def.key}`}
        />
      </div>
    );
  }

  // Select type → multi-select autocomplete
  if (def.type === 'select' || options.length > 0) {
    return (
      <MultiSelectField
        def={def}
        options={options}
        value={value}
        onChange={onChange}
      />
    );
  }

  // Plain text input
  return (
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value || null)}
      placeholder={def.label}
      className="h-7 text-xs w-36 border"
      style={{
        background: 'var(--vz-bg-card)',
        borderColor: 'var(--vz-border-subtle)',
        color: 'var(--vz-text-primary)',
      }}
      data-testid={`filter-${def.key}`}
    />
  );
}

// ── Multi-select popover field ─────────────────────────────────────────────────
function MultiSelectField({ def, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Current selections as array
  const selected = useMemo(
    () => (value ? value.split(',').filter(Boolean) : []),
    [value],
  );

  const normalizedOptions = useMemo(() =>
    options.map((opt) => {
      const isObj = opt && typeof opt === 'object';
      return {
        value: isObj ? opt.value : opt,
        label: isObj ? opt.label : opt,
      };
    }), [options]);

  const filtered = useMemo(() => {
    if (!search) return normalizedOptions;
    const s = search.toLowerCase();
    return normalizedOptions.filter((o) => o.label.toLowerCase().includes(s));
  }, [normalizedOptions, search]);

  const toggle = (optValue) => {
    const isSelected = selected.includes(optValue);
    const next = isSelected
      ? selected.filter((v) => v !== optValue)
      : [...selected, optValue];
    onChange(next.join(',') || null);
  };

  const triggerLabel = selected.length === 0
    ? def.label
    : selected.length === 1
      ? (normalizedOptions.find((o) => o.value === selected[0])?.label || selected[0])
      : `${def.label} (${selected.length})`;

  const isActive = selected.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1 border shrink-0 max-w-[200px]"
          style={{
            background: isActive ? 'rgba(74,158,255,0.08)' : 'var(--vz-bg-card)',
            borderColor: isActive ? 'rgba(74,158,255,0.4)' : 'var(--vz-border-subtle)',
            color: isActive ? '#4A9EFF' : 'var(--vz-text-secondary)',
          }}
          data-testid={`filter-${def.key}`}
        >
          <span className="truncate max-w-[140px]">{triggerLabel}</span>
          <ChevronDown size={10} className="shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', zIndex: 9999 }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={`Buscar ${def.label.toLowerCase()}...`}
            value={search}
            onValueChange={setSearch}
            className="text-xs h-8"
            style={{ color: 'var(--vz-text-primary)' }}
          />
          <CommandList style={{ maxHeight: '220px' }}>
            {isActive && (
              <CommandItem
                onSelect={() => { onChange(null); setSearch(''); }}
                className="text-xs text-rose-400 cursor-pointer"
              >
                <X size={10} className="mr-1" /> Limpiar selección
              </CommandItem>
            )}
            <CommandEmpty className="text-xs py-2 px-3" style={{ color: 'var(--vz-text-muted)' }}>
              Sin resultados
            </CommandEmpty>
            <CommandGroup>
              {filtered.map((opt) => {
                const isChecked = selected.includes(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.value}
                    onSelect={() => toggle(opt.value)}
                    className="text-xs cursor-pointer gap-2"
                    style={{ color: 'var(--vz-text-primary)' }}
                  >
                    <Checkbox
                      checked={isChecked}
                      className="h-3.5 w-3.5 shrink-0"
                      onCheckedChange={() => toggle(opt.value)}
                    />
                    <span className="truncate">{opt.label}</span>
                    {isChecked && <Check size={10} className="ml-auto shrink-0 text-blue-400" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ModuleFilterBar;
